import client from './base'

export interface Ville {
  id: number
  name: string
}

export interface TribunalType {
  id: number
  name: string
}

export interface AffectationProfile {
  id: string
  user: string
  user_name: string
  villes: Ville[]
  tribunal_types: TribunalType[]
  case_types: { id: number; name: string }[]
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface CreateProfileInput {
  user: string
  villes: number[]
  tribunal_types: number[]
  case_types: number[]
  is_active?: boolean
}

export interface UpdateProfileInput {
  villes: number[]
  tribunal_types: number[]
  case_types: number[]
  is_active?: boolean
}

export interface LawyerRecommendation {
  id: string
  name: string
  score: number
  is_recommended: boolean
  active_cases: number
  matching_rules: AffectationProfile[]
}

export interface AssignmentHistory {
  id: string
  user: string
  user_name: string
  context: 'case' | 'rendezvous'
  case: number | null
  case_id: number | null
  rendezvous: string | null
  assigned_by: string
  assigned_by_name: string
  created_at: string
}

export interface ProfilesListParams {
  limit?: number
  offset?: number
}

export interface ProfilesListResponse {
  count: number
  next: string | null
  previous: string | null
  results: AffectationProfile[]
}

export interface RecommendParams {
  ville_id?: number
  location_id?: number
  case_type_id?: number
  workload?: boolean
}

export interface HistoryListParams {
  user_id?: string
  context?: 'case' | 'rendezvous'
  limit?: number
  offset?: number
}

export interface HistoryListResponse {
  count: number
  next: string | null
  previous: string | null
  results: AssignmentHistory[]
}

export const affectationService = {
  listVilles: async (): Promise<Ville[]> => {
    const res = await client.get<Ville[]>('/api/v1/affectation/villes/')
    return res.data
  },

  searchTribunalTypes: async (search?: string): Promise<TribunalType[]> => {
    const res = await client.get<{ results: TribunalType[]; count: number }>('/api/v1/case/location/list', {
      params: { name: search || null, page_size: 30 },
    })
    return res.data.results
  },

  listProfiles: async (params?: ProfilesListParams): Promise<ProfilesListResponse> => {
    const res = await client.get<ProfilesListResponse>('/api/v1/affectation/profiles/', { params })
    return res.data
  },

  createProfile: async (data: CreateProfileInput): Promise<AffectationProfile> => {
    const res = await client.post<AffectationProfile>('/api/v1/affectation/profiles/', data)
    return res.data
  },

  updateProfile: async (id: string, data: UpdateProfileInput): Promise<AffectationProfile> => {
    const res = await client.patch<AffectationProfile>(`/api/v1/affectation/profiles/${id}/`, data)
    return res.data
  },

  toggleProfile: async (id: string): Promise<AffectationProfile> => {
    const res = await client.patch<AffectationProfile>(`/api/v1/affectation/profiles/${id}/toggle/`)
    return res.data
  },

  deleteProfile: async (id: string): Promise<void> => {
    await client.delete(`/api/v1/affectation/profiles/${id}/`)
  },

  recommend: async (params?: RecommendParams): Promise<{ count: number; results: LawyerRecommendation[] }> => {
    const res = await client.get<{ count: number; results: LawyerRecommendation[] }>('/api/v1/affectation/recommend/', { params })
    return res.data
  },

  listHistory: async (params?: HistoryListParams): Promise<HistoryListResponse> => {
    const res = await client.get<HistoryListResponse>('/api/v1/affectation/history/', { params })
    return res.data
  },
}
